import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  GestureEntropy,
  cast,
  isValidSeed,
  normalizeSeed,
  type CastMethod,
  type QrngResult,
  type Reading,
} from '@q-ching/core';

import { initialRitualState, ritualReducer } from './ritual/machine';
import { useReducedMotion } from './lib/hooks';
import {
  clearJournal,
  loadJournal,
  recordReading,
  type JournalEntry,
} from './lib/journal';

import { Threshold } from './phases/Threshold';
import { Question } from './phases/Question';
import { Gathering } from './phases/Gathering';
import { Casting } from './phases/Casting';
import { Reading as ReadingPhase } from './phases/Reading';
import { Journal } from './components/Journal';
import { SmokeField } from './components/SmokeField';
import { LanguageSelector } from './components/LanguageSelector';
import { Footer } from './components/Footer';
import { About } from './components/About';
import { Analysis } from './components/Analysis';

const phaseFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] as const } },
  exit: { opacity: 0, transition: { duration: 0.7, ease: 'easeInOut' as const } },
};

export default function App() {
  const reducedMotion = useReducedMotion();
  const [state, dispatch] = useReducer(ritualReducer, initialRitualState);

  // One GestureEntropy instance per ritual run. We re-create it on each fresh
  // run (begin / cast again) so each reading is stirred from a clean slate.
  const gestureRef = useRef(new GestureEntropy());
  const [, forceGestureKey] = useState(0);

  // Quantum results captured during GATHERING, handed to CASTING.
  const qrngRef = useRef<QrngResult[]>([]);
  // Snapshot of the gesture bytes taken at the moment of casting.
  const userEntropyRef = useRef<Uint8Array>(new Uint8Array());

  // Static "pages" rendered over the ritual (footer → About → Analysis).
  const [overlay, setOverlay] = useState<'about' | 'analysis' | null>(null);

  // Journal
  const [journalOpen, setJournalOpen] = useState(false);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  useEffect(() => {
    setEntries(loadJournal());
  }, []);

  // Replay-from-link: opening the page with ?seed=…(&method=…) reproduces that
  // exact cast and jumps straight to the reading. Like a journal revisit, we
  // don't re-journal it — it may be someone else's shared cast. A malformed or
  // missing seed simply falls through to the threshold.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawSeed = params.get('seed');
    if (!rawSeed || !isValidSeed(rawSeed)) return;
    const method: CastMethod = params.get('method') === 'yarrow' ? 'yarrow' : 'coin';
    void (async () => {
      try {
        const reading = await cast({ seed: normalizeSeed(rawSeed), method });
        dispatch({ type: 'setMethod', method });
        dispatch({ type: 'castComplete', reading });
      } catch {
        /* unreproducible seed — leave the querent at the threshold */
      }
    })();
  }, []);

  // Keep the address bar in step with the reading on screen, so it is always a
  // shareable link; strip the params when we leave the reading. replaceState
  // keeps this out of the back-button history.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (state.phase === 'reading' && state.reading) {
      url.searchParams.set('seed', state.reading.seed);
      url.searchParams.set('method', state.reading.method);
    } else {
      url.searchParams.delete('seed');
      url.searchParams.delete('method');
    }
    window.history.replaceState(null, '', url);
  }, [state.phase, state.reading]);

  const freshGesture = useCallback(() => {
    gestureRef.current = new GestureEntropy();
    forceGestureKey((n) => n + 1);
  }, []);

  // ---- phase transitions ----------------------------------------------------

  const handleBegin = useCallback(() => {
    freshGesture();
    dispatch({ type: 'begin' });
  }, [freshGesture]);

  const handleGather = useCallback(() => {
    dispatch({ type: 'toGathering' });
  }, []);

  const handleCast = useCallback((qrng: QrngResult[]) => {
    qrngRef.current = qrng;
    userEntropyRef.current = gestureRef.current.bytes;
    dispatch({ type: 'toCasting' });
  }, []);

  const handleCastComplete = useCallback(
    (reading: Reading) => {
      recordReading(state.question, reading);
      setEntries(loadJournal());
      dispatch({ type: 'castComplete', reading });
    },
    [state.question],
  );

  const handleAgain = useCallback(() => {
    freshGesture();
    dispatch({ type: 'again' });
  }, [freshGesture]);

  // Revisit a journal entry: reproduce the cast deterministically from its seed.
  const handleRevisit = useCallback(async (entry: JournalEntry) => {
    setJournalOpen(false);
    try {
      const reading = await cast({ seed: entry.seed, method: entry.method });
      dispatch({ type: 'setQuestion', question: entry.question });
      // Jump straight to the reading without re-running the gather animation.
      dispatch({ type: 'castComplete', reading });
    } catch (err) {
      console.error('Could not reproduce reading from seed', err);
    }
  }, []);

  const clearAndClose = useCallback(() => {
    clearJournal();
    setEntries([]);
  }, []);

  // ---- render ---------------------------------------------------------------
  // Rendered fresh each pass; the surrounding AnimatePresence keys on phase, and
  // the gesture instance is read live from the ref so a fresh run is picked up.

  function renderPhase() {
    switch (state.phase) {
      case 'threshold':
        return <Threshold onBegin={handleBegin} />;
      case 'question':
        return (
          <Question
            question={state.question}
            method={state.method}
            gesture={gestureRef.current}
            sources={state.sources}
            onQuestionChange={(q) => dispatch({ type: 'setQuestion', question: q })}
            onMethodChange={(m) => dispatch({ type: 'setMethod', method: m })}
            onSourcesChange={(s) => dispatch({ type: 'setSources', sources: s })}
            onContinue={handleGather}
          />
        );
      case 'gathering':
        return (
          <Gathering
            gesture={gestureRef.current}
            reducedMotion={reducedMotion}
            sources={state.sources}
            onCast={handleCast}
          />
        );
      case 'casting':
        return (
          <Casting
            method={state.method}
            userEntropy={userEntropyRef.current}
            qrng={qrngRef.current}
            reducedMotion={reducedMotion}
            onComplete={handleCastComplete}
          />
        );
      case 'reading':
        return state.reading ? (
          <ReadingPhase
            reading={state.reading}
            question={state.question}
            reducedMotion={reducedMotion}
            onAgain={handleAgain}
          />
        ) : null;
      default:
        return null;
    }
  }

  return (
    <>
      <div className="atmosphere" aria-hidden />
      <SmokeField
        reducedMotion={reducedMotion}
        seed={state.reading?.seed}
        emit={state.phase === 'gathering'}
      />

      <LanguageSelector />
      <Footer onAbout={() => setOverlay('about')} />

      <About
        open={overlay === 'about'}
        reducedMotion={reducedMotion}
        onClose={() => setOverlay(null)}
        onOpenAnalysis={() => setOverlay('analysis')}
      />
      <Analysis
        open={overlay === 'analysis'}
        reducedMotion={reducedMotion}
        onClose={() => setOverlay('about')}
      />

      <Journal
        open={journalOpen}
        entries={entries}
        reducedMotion={reducedMotion}
        onOpen={() => setJournalOpen(true)}
        onClose={() => setJournalOpen(false)}
        onClear={clearAndClose}
        onRevisit={handleRevisit}
      />

      <AnimatePresence mode="wait">
        <motion.main
          key={state.phase}
          variants={phaseFade}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {renderPhase()}
        </motion.main>
      </AnimatePresence>
    </>
  );
}
